import React, { type ReactNode } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { Reorder } from 'framer-motion'
import { partition } from 'lodash'

import Question from 'modules/questionnaires/components/Question'
import Section from 'modules/questionnaires/components/Section'
import { isSection } from 'modules/questionnaires/lib/helpers'
import {
  QuestionnaireContent,
  QuestionnaireElement,
  QuestionnaireElementType,
  Translations,
} from 'modules/questionnaires/lib/types'

import Actions, { type Action } from './Actions'
import EmptyState from './EmptyState'

export interface Props {
  content: QuestionnaireContent
  translations: Translations
  onChange: (content: QuestionnaireContent) => Promise<QuestionnaireContent> | void
  showActions?: boolean
  formType?: string
  extraActions?: Action[]
  renderSectionHeaderAddon?: (sectionId: string) => ReactNode | undefined
  renderSectionFooter?: (sectionId: string) => ReactNode | undefined
}

const Questionnaire: React.FC<Props> = ({
  content = [],
  translations,
  onChange,
  showActions = true,
  formType,
  extraActions = [],
  renderSectionHeaderAddon,
  renderSectionFooter,
}) => {
  const [currentForm, setCurrentForm] = React.useState<QuestionnaireElementType | undefined>()
  const [lastDuplicated, setLastDuplicated] = React.useState<string | undefined>(undefined)
  const [lastCreated, setLastCreated] = React.useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = React.useState(false)

  const [lockedContent, editableContent] = partition(content, 'locked')
  const [isDragging, setIsDragging] = React.useState(false)

  const updateContent = async (updatedContent: QuestionnaireContent) => {
    await onChange(updatedContent.concat(lockedContent))
  }

  const handleNewItem = (type: 'section' | 'question') => {
    setCurrentForm(type)
  }

  const handleChange = async (updatedContent: QuestionnaireContent) => {
    setIsLoading(true)
    try {
      await updateContent(updatedContent)
    } catch (error) {
      // consumers can handle errors on their own because handleFormSubmit is an async wrapper
    } finally {
      setIsLoading(false)
      setCurrentForm(undefined)
    }
  }

  const handleAddItem = (item: QuestionnaireElement) => {
    setLastCreated(item.uuid)
    handleChange([...editableContent, item])
  }

  const handleUpdateItem = (item: QuestionnaireElement) => {
    const updatedContent = editableContent.map((i) => (i.uuid === item.uuid ? item : i))
    handleChange(updatedContent)
  }

  const handleDuplicateItem = (item: QuestionnaireElement) => {
    handleChange([...editableContent, item])
    setLastDuplicated(item.uuid)
  }

  const handleSaveAndDuplicateItems = (items: QuestionnaireElement[]) => {
    handleChange([...editableContent, ...items])
    const id = items[items.length - 1]?.uuid
    setLastDuplicated(id)
  }

  const handleDeleteItem = (item: QuestionnaireElement) => {
    const updatedContent = editableContent.filter((i) => i.uuid !== item.uuid)
    handleChange(updatedContent)
  }

  const actions: Action[] = showActions
    ? [
        {
          label: translations.questionnaire.add_question,
          icon: 'add',
          type: 'secondary',
          onClick: () => handleNewItem('question'),
          disabled: isLoading,
          ariaLabel: translations.questionnaire.add_question,
        },
        {
          label: translations.questionnaire.add_section,
          icon: 'align-text-justify',
          type: 'primary',
          onClick: () => handleNewItem('section'),
          disabled: isLoading,
          ariaLabel: translations.questionnaire.add_section,
        },
        ...extraActions.map((action) => ({ ...action, disabled: action.disabled || isLoading })),
      ]
    : []

  if (!content.length && !currentForm) {
    return (
      <EmptyState
        title={translations.questionnaire.empty_state.title}
        description={translations.questionnaire.empty_state.description}
        illustration="page-edit"
        actions={actions}
      />
    )
  }

  const handleDragStart = () => {
    setIsDragging(true)
  }
  const handleDragEnd = () => {
    setTimeout(() => setIsDragging(false), 0)
  }

  return (
    <Box gap="s24">
      <Reorder.Group values={editableContent} onReorder={updateContent}>
        <Box gap="s24">
          {editableContent.map((item) => {
            return (
              <Reorder.Item
                key={item.uuid}
                value={item}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                {isSection(item) ? (
                  <Section
                    renderHeaderAddon={renderSectionHeaderAddon}
                    disabled={isLoading}
                    collapsed={item.uuid !== lastCreated}
                    translations={translations}
                    editing={lastDuplicated === item.uuid}
                    section={item}
                    onSave={handleUpdateItem}
                    onDuplicate={handleDuplicateItem}
                    onDelete={handleDeleteItem}
                  />
                ) : (
                  <Question
                    translations={translations.question}
                    disabled={isLoading}
                    editing={lastDuplicated === item.uuid}
                    question={item}
                    onSave={handleUpdateItem}
                    onDuplicate={handleDuplicateItem}
                    onDelete={handleDeleteItem}
                    isDragging={isDragging}
                  />
                )}
              </Reorder.Item>
            )
          })}
        </Box>
      </Reorder.Group>

      {currentForm === 'section' && (
        <Section
          editing
          collapsed={false}
          disabled={isLoading}
          translations={translations}
          onSave={handleAddItem}
          onDuplicate={handleDuplicateItem}
          onDelete={handleDeleteItem}
          onSaveAndDuplicate={handleSaveAndDuplicateItems}
        />
      )}
      {currentForm === 'question' && (
        <Question
          editing
          disabled={isLoading}
          translations={translations.question}
          onSave={handleAddItem}
          onDelete={() => setCurrentForm(undefined)}
          onDuplicate={handleDuplicateItem}
          onSaveAndDuplicate={handleSaveAndDuplicateItems}
        />
      )}

      {lockedContent.map(
        (item) =>
          isSection(item) && (
            <Section
              key={item.uuid}
              section={item}
              collapsed={item.uuid !== lastCreated}
              translations={translations}
              locked
              formType={formType}
              renderHeaderAddon={renderSectionHeaderAddon}
              renderSectionFooter={renderSectionFooter}
            />
          )
      )}

      {showActions && <Actions actions={actions} />}
    </Box>
  )
}
export default Questionnaire
